import { useEffect, useMemo, useState } from 'react';
import { debounce, isNil } from 'lodash';

import useDataFetcher from '~/components/useDataFetcher';

interface PolicyLimitBreach {
  reason: string;
}

interface FetchReservePaymentPossibilityRet {
  isLoadingReservePaymentIssues: boolean;
  isErrorReservePaymentIssues: boolean;
  reservePolicyLimitBreaches: PolicyLimitBreach[];
  isReservePaymentIssuePreventingReserve: boolean | null;
}

interface ReservePaymentPossibilityResponse {
  is_valid: boolean | null;
  policy_limit_breaches: PolicyLimitBreach[];
  approve_option: string | null;
}

interface ReservePaymentRequestParams {
  amount: number;
  payment_type: string;
  include_reserve: boolean;
  amount_replaces_exposure_reserve: boolean;
  should_include_pending_payments: boolean;
}

export default function useFetchReservePaymentPossibility(
  claimId: number,
  exposureIdOrNull: number | null,
  payableType: string,
  reserveAmount: number | string
): FetchReservePaymentPossibilityRet {
  // Convert input to number for internal use
  const numericReserveAmount = Number(reserveAmount);

  // State for debounced reserve amount
  const [debouncedReserveAmount, setDebouncedReserveAmount] = useState(numericReserveAmount);

  // Create debounced function to update reserve amount
  const debouncedSetReserveAmount = useMemo(
    () =>
      debounce((amount: number) => {
        setDebouncedReserveAmount(amount);
      }, 500), // 500ms debounce like ContactSearch
    []
  );

  // Update debounced amount when reserveAmount changes
  useEffect(() => {
    debouncedSetReserveAmount(numericReserveAmount);
  }, [numericReserveAmount, debouncedSetReserveAmount]);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSetReserveAmount.cancel();
    };
  }, [debouncedSetReserveAmount]);

  // Only fetch for indemnity reserves with positive amounts
  const shouldFetch = !isNil(exposureIdOrNull) && debouncedReserveAmount > 0 && payableType === 'indemnity';

  // Immediate UX: Clear warnings instantly when user types 0 (don't wait 500ms debounce)
  const isCurrentAmountInvalid = numericReserveAmount <= 0 || isNaN(numericReserveAmount);
  const shouldShowCleanImmediately = isCurrentAmountInvalid || isNil(exposureIdOrNull) || payableType !== 'indemnity';

  // Build request parameters for reserve possibility check.
  // include_reserve puts other exposures' outstanding reserves into the incurred total;
  // amount_replaces_exposure_reserve takes THIS exposure's current reserve back out, because the
  // amount being validated replaces it rather than adding to it.
  const params: ReservePaymentRequestParams = useMemo(
    () => ({
      amount: debouncedReserveAmount,
      payment_type: payableType,
      include_reserve: true,
      amount_replaces_exposure_reserve: true,
      should_include_pending_payments: false,
    }),
    [debouncedReserveAmount, payableType]
  );

  const {
    isLoading: isLoadingReservePaymentIssues,
    isError: isErrorReservePaymentIssues,
    data: {
      is_valid: reserveIsValid,
      policy_limit_breaches: reservePolicyLimitBreaches,
      approve_option: reservePaymentIssuesApproveOption,
    } = {
      is_valid: null,
      policy_limit_breaches: [],
      approve_option: null,
    },
  } = useDataFetcher<ReservePaymentPossibilityResponse>(
    shouldFetch ? `/api/v1/claims/${claimId}/exposures/${exposureIdOrNull}/payment_possibility` : '',
    { params },
    shouldFetch
  );

  const isReservePaymentIssuePreventingReserve = !reserveIsValid && reservePaymentIssuesApproveOption === 'none';

  // Return clean state immediately when not applicable or amount is invalid
  if (shouldShowCleanImmediately || !shouldFetch) {
    return {
      isLoadingReservePaymentIssues: false,
      isErrorReservePaymentIssues: false,
      reservePolicyLimitBreaches: [],
      isReservePaymentIssuePreventingReserve: null,
    };
  }

  return {
    isLoadingReservePaymentIssues,
    isErrorReservePaymentIssues,
    reservePolicyLimitBreaches,
    isReservePaymentIssuePreventingReserve,
  };
}
